import { useState, useEffect, useCallback } from 'react';
import { useWebSocketContext } from '../contexts/WebSocketContext';

export const useWebSocket = (matchId) => {
  const { socket, connected } = useWebSocketContext();
  console.log('[useWebSocket] Hook called - matchId:', matchId, 'socket:', !!socket, 'socket.connected:', socket?.connected);
  const [events, setEvents] = useState([]);
  const [score, setScore] = useState({ home: 0, away: 0 });
  const [viewers, setViewers] = useState(0);
  const [matchStatus, setMatchStatus] = useState('scheduled');
  const [joined, setJoined] = useState(false);
  const [clock, setClock] = useState({
    minute: 0,
    second: 0,
    half: 1,
    injuryTime: 0,
    running: false
  });

  // Join match on mount
  useEffect(() => {
    if (!socket || !matchId) return;

    const joinMatch = () => {
      if (!socket || !socket.connected) {
        console.log('[Match] Waiting for connection...');
        return;
      }
      console.log('[Match] Joining match:', matchId);
      socket.emit('match:join', { matchId });
    };

    // CRITICAL: Register 'connect' listener BEFORE checking if already connected
    // This ensures we catch the connection event regardless of timing
    socket.on('connect', () => {
      console.log('[Match] Socket connected, attempting join');
      joinMatch();
    });

    socket.on('match:joined', (data) => {
      console.log('[Match] Joined:', data);
      setJoined(true);
      setViewers(data.viewers);

      // Initialize clock from state
      if (data.state?.clock) {
        setClock(data.state.clock);
      }

      // Initialize score from state
      if (data.state?.score) {
        setScore(data.state.score);
      }

      // Initialize events from state
      if (data.state?.events && data.state.events.length > 0) {
        setEvents(data.state.events);
      }
    });

    socket.on('match:error', (error) => {
      console.error('[Match] Error:', error);
    });

    // Check if already connected (after listeners are registered)
    if (socket?.connected) {
      console.log('[Match] Socket already connected, joining immediately');
      joinMatch();
    }

    // Cleanup
    return () => {
      if (socket && matchId) {
        socket.emit('match:leave', { matchId });
        socket.off('match:joined');
        socket.off('match:error');
        socket.off('connect');
      }
    };
  }, [socket, matchId]); // Remove 'connected' from dependencies

  // Listen to match events
  useEffect(() => {
    if (!socket || !joined) return;

    // New event
    socket.on('match:event:new', (data) => {
      console.log('[Match] New event:', data);
      setEvents(prev => [...prev, data.event]);
    });

    // Score update
    socket.on('match:score:updated', (data) => {
      console.log('[Match] Score updated:', data);
      setScore({ home: data.homeScore, away: data.awayScore });
    });

    // Event updated
    socket.on('match:event:updated', (data) => {
      console.log('[Match] Event updated:', data);
      setEvents(prev => prev.map(e => e.id === data.event.id ? data.event : e));
    });

    // Event deleted
    socket.on('match:event:deleted', (data) => {
      console.log('[Match] Event deleted:', data);
      setEvents(prev => prev.filter(e => e.id !== data.eventId));
    });

    // Viewer changes
    socket.on('match:viewer:joined', (data) => {
      setViewers(data.viewers);
    });

    socket.on('match:viewer:left', (data) => {
      setViewers(data.viewers);
    });

    // Clock updates
    socket.on('match:clock:update', (data) => {
      setClock({
        minute: data.minute,
        second: data.second,
        half: data.half,
        injuryTime: data.injuryTime,
        running: data.running
      });
    });

    return () => {
      socket.off('match:event:new');
      socket.off('match:score:updated');
      socket.off('match:event:updated');
      socket.off('match:event:deleted');
      socket.off('match:viewer:joined');
      socket.off('match:viewer:left');
      socket.off('match:clock:update');
    };
  }, [socket, joined]);

  // Methods to emit events
  const createEvent = useCallback((eventData) => {
    if (!socket || !joined) return;
    
    socket.emit('match:event:create', {
      matchId,
      ...eventData
    });
  }, [socket, joined, matchId]);

  const updateScore = useCallback((homeScore, awayScore) => {
    if (!socket || !joined) return;
    
    socket.emit('match:score', {
      matchId,
      homeScore,
      awayScore
    });
  }, [socket, joined, matchId]);

  const updateEvent = useCallback((eventId, updateData) => {
    if (!socket || !joined) return;
    
    socket.emit('match:event:update', {
      eventId,
      ...updateData
    });
  }, [socket, joined]);

  const deleteEvent = useCallback((eventId) => {
    if (!socket || !joined) return;
    
    socket.emit('match:event:delete', {
      eventId,
      matchId
    });
  }, [socket, joined, matchId]);

  return {
    connected,
    joined,
    events,
    score,
    viewers,
    matchStatus,
    clock,
    createEvent,
    updateScore,
    updateEvent,
    deleteEvent
  };
};
